
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ModalEditStudentData from '@/components/templates/manage/ModalEditStudentData.vue'
import { GradeCode } from '@/const/GradeCode'
import { SelectItem } from '@/models/SelectItem'
import { ApiNotFoundAppError } from '@/models/AppErrorClasses'
import { ApiResponseStudent } from '@/api/responses/manage/userStudents'
import { AcademyCoursesApi } from '@/api/methods/manage/AcademyCoursesApi'
import { AcademyClassesApi } from '@/api/methods/manage/AcademyClassesApi'
import { UserStudentsApi } from '@/api/methods/manage/UserStudentsApi'

/**
 * 生徒情報更新モーダル
 */
@Component({
  components: {
    ModalEditStudentData,
  },
})
export default class ModalEditStudent extends Vue {
  /** 生徒情報更新モーダルのフォーマット */
  @Ref()
  modalEditStudentData!: ModalEditStudentData

  /** 生徒情報更新後の処理 */
  @Prop()
  private afterEditStudent!: () => Promise<void>

  /** 学年一覧 */
  private gradeList: SelectItem[] = []

  /** 学年選択の初期値 */
  private defaultGradeCode = ''

  /** コース一覧 */
  private courseList: SelectItem[] = []

  /** コース選択の初期値 */
  private defaultCourseCode = ''

  /** クラス一覧 */
  private classList: SelectItem[] = []

  /** クラス選択の初期値 */
  private defaultClassCode = ''

  /** ユーザのクッキー情報 */
  private userAcademy = this.$appCookies.authAcademy.get()

  /** 対象生徒のユーザID */
  private targetUserId = 0

  /**
   * 確認ボタン押下時の処理
   */
  private async submit(): Promise<void> {
    this.$loading.start()

    // 生徒情報更新
    await UserStudentsApi.updateStudent(
      this.targetUserId,
      this.modalEditStudentData.nickname,
      this.modalEditStudentData.identifyTag,
      this.modalEditStudentData.gradeSelect.selectedValue(),
      Number(this.modalEditStudentData.courseSelect.selectedValue()),
      Number(this.modalEditStudentData.classSelect.selectedValue())
    )

    // 生徒情報更新後の処理
    //   メソッド内で生徒一覧の再取得を行う
    await this.afterEditStudent()

    this.$loading.complete()

    // モーダルを閉じる
    this.hide()
  }

  /**
   * モーダルを開く
   *
   * @param userId ユーザID
   */
  public async show(userId: number): Promise<void> {
    // ユーザIDを設定
    this.targetUserId = userId

    this.$loading.start()

    // 生徒情報取得
    let targetStudent!: ApiResponseStudent
    try {
      targetStudent = await UserStudentsApi.getStudent(userId)
    } catch (error) {
      if (error instanceof ApiNotFoundAppError) {
        alert('指定ユーザが存在しません。')
        this.$loading.complete()
        return
      }

      // 404エラー以外の場合は例外を投げ直し、共通処理に渡す
      throw error
    }

    // 学年選択設定
    this.setGradeList()
    // 学年の初期値を設定
    this.defaultGradeCode = targetStudent.gradeCode

    // コース選択設定
    await this.setCourseList(targetStudent.courseName)

    // クラス選択設定
    await this.setClassList(this.defaultCourseCode, targetStudent.className)

    // モーダルを開く
    this.modalEditStudentData.show(targetStudent.nickname, targetStudent.identifyTag)

    this.$loading.complete()
  }

  /**
   * 学年一覧の設定
   */
  private setGradeList(): void {
    // 学年プルダウン設定
    this.gradeList = GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })
  }

  /**
   * コース選択の設定
   *
   * @param courseName コース名
   */
  private async setCourseList(courseName: string): Promise<void> {
    // コース一覧取得
    const options = { academyId: this.userAcademy.academyId }
    const courses = await AcademyCoursesApi.getAll(options)

    // コース選択設定
    this.courseList = courses.map((course) => {
      return {
        // コースIDをコードとして使用
        value: String(course.id),
        label: course.name,
      }
    })

    const courseCode = this.courseList.find((course) => course.label === courseName)?.value

    this.defaultCourseCode = courseCode || this.courseList[0].value
  }

  /**
   * クラス選択の設定
   *
   * @param courseCode コース指定
   * @param className クラス名
   */
  private async setClassList(courseCode: string, className?: string): Promise<void> {
    // コースコードが存在しない場合
    // 運用上発生しないが予期せぬエラー回避のため記述
    if (!courseCode) {
      this.classList = [{ label: '', value: '' }]
      return
    }

    // クラス一覧取得
    const classes = await AcademyClassesApi.getAll(Number(courseCode))

    // クラス選択設定
    this.classList = classes.map((academyClass) => {
      return {
        // クラスIDをコードとして使用
        value: String(academyClass.id),
        label: academyClass.name,
      }
    })

    const classCode = this.classList.find((academyClass) => academyClass.label === className)?.value

    this.defaultClassCode = classCode || this.classList[0].value
  }

  /**
   * コース選択変更時のイベント
   */
  private async changeCourseSelect(): Promise<void> {
    this.$loading.start()

    // クラス選択設定
    await this.setClassList(this.modalEditStudentData.courseSelect.selectedValue())
    // クラスの選択値を更新
    this.modalEditStudentData.classSelect.setCurrentValue(this.classList[0].value)

    this.$loading.complete()
  }

  /**
   * モーダルを閉じる
   */
  private hide(): void {
    this.modalEditStudentData.hide()
  }
}
